import { ChangeDetectorRef, Component, DestroyRef, Injector, OnInit, inject, input } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, ActivationStart, Router } from '@angular/router';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ThemeBorder, ThemeElevated } from '@frontend/sports/common/ui/shared';
import { AccordionVariant, SportsThemedAccordionDirective } from '@frontend/sports/common/ui/sports-themed-accordion';
import { SportsThemedCardDirective } from '@frontend/sports/common/ui/sports-themed-card';
import { DsAccordionModule } from '@frontend/ui/accordion';
import { DsCard } from '@frontend/ui/card';
import { DsDivider } from '@frontend/ui/divider';
import { DeviceService, MediaQueryService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { assign, includes } from 'lodash-es';
import { Observable, distinctUntilChanged, filter, from, map, switchMap } from 'rxjs';

import { SeoContentService } from './seo-content.service';
import { SeoContentApiResponse } from './seo.models';

@Component({
    selector: 'ms-content-box',
    templateUrl: '././content-box.component.html',
    styleUrl: './content-box.component.scss',
    imports: [IconCustomComponent, DsCard, DsAccordionModule, DsDivider, SportsThemedAccordionDirective, SportsThemedCardDirective],
    host: { class: 'seo-content-box-scope' },
})
export class ContentBoxComponent implements OnInit {
    sitecore = inject(Sitecore);
    private seoContentService = inject(SeoContentService);
    private routerEventsService = inject(RouterEventsService);
    private router = inject(Router);
    private meta = inject(Meta);
    private title = inject(Title);
    private changeDetectionRef = inject(ChangeDetectorRef);
    private _sanitizer = inject(DomSanitizer);
    private injector = inject(Injector);

    routeChanging = false;
    header: string;
    contentSeo: SafeHtml;
    primaryHeader: boolean;
    collapsed = false;
    userClicked = false;
    readonly event = input<EventModel | undefined>();

    private readonly tagName = 'description';
    private readonly destroyRef = inject(DestroyRef);
    readonly isEpcotEnabled = inject(EpcotConfig).isEnabled;
    readonly device = inject(DeviceService);
    readonly isMobileMode = this.device.isMobile;
    readonly themedAccordionVariant: AccordionVariant = {
        'sportingbet-br': 'surface-low',
        'bwin-dark': 'surface-low',
        'sports-interaction': 'surface-highest',
    };
    readonly themeCardElevated: ThemeElevated = { 'sportingbet-br': { isElevated: false } };
    readonly themeCardBorder: ThemeBorder = { 'bwin': { hasBorder: false }, 'bwin-dark': { hasBorder: false } };

    constructor() {
        const mediaService = inject(MediaQueryService);

        mediaService
            .observe()
            .pipe(
                filter(() => !this.userClicked && !this.device.isRobot),
                map((queries) => 'gt-md' in queries.breakpoints && queries.breakpoints['gt-md']),
                distinctUntilChanged(),
                takeUntilDestroyed(),
            )
            .subscribe(() => (this.collapsed = true));
    }

    ngOnInit(): void {
        toObservable(this.event, {
            injector: this.injector,
        })
            .pipe(
                switchMap((event) => {
                    if (!event) {
                        return this.routerEventsService.currentActivationEnd.pipe(
                            filter(isDefined),
                            switchMap((e) => from(this.getSeoContent(e.snapshot))),
                        );
                    }

                    return this.getSeoContentForEvent(event);
                }),
            )
            .pipe(filter(isDefined), takeUntilDestroyed(this.destroyRef))
            .subscribe((seoContent) => this.updateSeoContent(seoContent));

        this.router.events
            .pipe(
                filter((e) => e instanceof ActivationStart),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => (this.routeChanging = true));

        this.meta.addTag({ name: this.tagName });
    }

    toggle(): void {
        this.collapsed = !this.collapsed;
        this.userClicked = true;
    }

    private getSeoContentForEvent(event: EventModel): Observable<SeoContentApiResponse | undefined> {
        return this.seoContentService.getForEventDetails({
            lobby: false,
            live: event.live,
            highlights: false,
            allSports: false,
            sport: event.sport.id,
            region: event.region.id,
            league: event.league?.id,
            event: event.id,
        });
    }

    private getSeoContent(routeSnapshot: ActivatedRouteSnapshot): Observable<SeoContentApiResponse | undefined> {
        this.cleanContent();

        if (routeSnapshot.data.tag === RouteTag.EventDetails) {
            const event = routeSnapshot.data.model.model as EventModel;
            if (event) {
                return this.getSeoContentForEvent(event);
            }
        }

        const params = assign({ path: routeSnapshot.url.map((segment) => `/${segment}`).join('') }, routeSnapshot.params);
        const tagOf = (...sources: RouteTag[]) => includes(sources, routeSnapshot.data.tag);

        return this.seoContentService.getSeoContent(params, {
            liveHighlights: tagOf(RouteTag.LiveHighlights),
            sportsList: tagOf(RouteTag.LiveAll, RouteTag.SportList),
            sportsLobby: tagOf(RouteTag.Lobby),
            virtual: tagOf(RouteTag.VirtualFixture),
            live: tagOf(
                RouteTag.Live,
                RouteTag.LiveAll,
                RouteTag.LiveVideo,
                RouteTag.LiveHighlights,
                RouteTag.LiveSport,
                RouteTag.LiveEsports,
                RouteTag.LiveRegion,
            ),
            liveVideo: tagOf(RouteTag.LiveVideo),
            esportsLobby: tagOf(RouteTag.EsportsLobby),
            esportsHighlights: tagOf(RouteTag.LiveEsports),
            multiSportsLobby: tagOf(RouteTag.MultiSportsLobby),
        });
    }

    private updateSeoContent(seoContent: SeoContentApiResponse): void {
        this.routeChanging = false;

        this.header = seoContent.header || '';
        this.primaryHeader = !seoContent.h1Included;
        this.contentSeo = seoContent.content ? this._sanitizer.bypassSecurityTrustHtml(seoContent.content) : '';

        this.title.setTitle(seoContent.metaTitle || '');
        this.meta.updateTag({ name: this.tagName, content: seoContent.metaDescription || '' });
        this.changeDetectionRef.markForCheck();
    }

    private cleanContent(): void {
        this.header = '';
        this.contentSeo = '';
    }
}
